import type { EstimateConfidence, EstimateConfidenceLevel } from '@lizrd/contracts';
import { Modal } from './ui/modal';
import { usd } from '../lib/graph-visuals';

/** Presentation for each confidence band — a subtle dot colour + plain wording. */
export const CONFIDENCE_META: Record<
  EstimateConfidenceLevel,
  { label: string; dot: string; blurb: string }
> = {
  high: {
    label: 'High',
    dot: 'var(--iris)',
    blurb: 'Our estimate closely tracks your actual billed cost.',
  },
  medium: {
    label: 'Medium',
    dot: 'var(--warning)',
    blurb: 'Our estimate is in the right range but diverges somewhat from your actual bill.',
  },
  low: {
    label: 'Low',
    dot: 'var(--danger)',
    blurb:
      'Our estimate differs noticeably from your actual bill so far — usually because resources are new (partial billing period), free-tier eligible, or on committed-use discounts. It converges as a full month accrues.',
  },
  unknown: {
    label: 'Calibrating',
    dot: 'var(--muted)',
    blurb:
      'Not enough billing history yet to calibrate — a confidence rating appears once your cloud reports a billing signal.',
  },
};

/** The short chip label shown next to the cost figure. */
export function confidenceChipLabel(level: EstimateConfidenceLevel): string {
  return level === 'unknown' ? 'Calibrating' : `${CONFIDENCE_META[level].label} confidence`;
}

/**
 * Explains the monthly cost figure and how much we trust it (CT-S1, reframed).
 * Opened from the subtle confidence chip under "Est. Monthly Cost". Shows the
 * estimate, our confidence band, and — when available — the actual billed cost
 * behind that rating, in plain language (no raw ratios).
 */
export function EstimateConfidenceModal({
  data,
  onClose,
}: {
  data: EstimateConfidence;
  onClose: () => void;
}) {
  const meta = CONFIDENCE_META[data.level];
  return (
    <Modal
      open
      onClose={onClose}
      title={
        <h3 className="font-display text-base font-bold text-bone">About this cost estimate</h3>
      }
    >
      <div className="space-y-4 text-sm">
        <div>
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">
            Estimated Monthly Cost
          </div>
          <div className="font-display text-2xl font-bold text-iris">
            {usd(data.estimatedMonthlyCost)}
            <span className="text-sm text-muted">/mo</span>
          </div>
          <p className="mt-1 text-xs text-muted">
            A run-rate from our per-resource model — each resource priced at on-demand list rate for
            a full month.
          </p>
        </div>

        <div className="rounded-md border border-hairline bg-surface-2/40 p-3">
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: meta.dot }}
            />
            <span className="font-semibold text-bone">{meta.label} confidence</span>
          </div>
          <p className="mt-1.5 text-xs leading-snug text-haze">{meta.blurb}</p>
        </div>

        {data.billedMonthlyCost != null && (
          <div className="flex items-center justify-between rounded-md border border-hairline p-3 text-xs">
            <span className="text-muted">Actual billed (trailing 30 days)</span>
            <span className="font-display text-bone">{usd(data.billedMonthlyCost)}/mo</span>
          </div>
        )}

        <p className="text-[0.7rem] leading-snug text-muted">
          We derive confidence by comparing our estimate against the real cost your cloud reports.
          Usage-based charges (data transfer, S3, Lambda) and discounts aren’t in the per-resource
          estimate, so the two can differ — the rating reflects how closely they currently agree.
        </p>
      </div>
    </Modal>
  );
}
